import { FC, useMemo, useEffect, memo, useContext } from 'react'
import { GroupedVirtuoso } from 'react-virtuoso'
import { ScreenContext } from 'react-components'
import { Container, SectionHeading, ListItemWrapper, GridRowWrapper, SectionSpacer } from './styles'
import { LIST_VIEW_CONFIG, SECTION_MERGE_KEYS } from '../../../../constants/search-result-listing'
import ListingCard from './listing-card'
import SkeletonRow from './skeleton-row'
import ListingHeader from '../listing-header'
import ListingNoData from './listing-no-data'
import ListingErrorCard from './listing-error-card'
import useHybridListings from '../../../../hooks/hybrid-listings'
import { useRequestUserConfigContext } from '../../../../hooks/request'
import { useFetchSRLListings, useFetchHoliduListings, useHiddenGemListings } from './hooks'
import { useABVariant } from '../../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../../common/constants'
import HiddenGemCarousel from './hidden-gem-carousel'
import { BreadcrumbListTransport } from '../../../../models/common/breadcrumb-model'
import { SuitablesModel } from '../../../../models/common/suitables-model'
import { Offer } from '../../../../models/common/offers-model'
import { ListingSectionObject } from '../../../../transports/common/listing-section-transport'
import { useTranslation } from '../../../../hooks/locale'
import { VirtuosoFooter, VirtuosoList } from './virtuoso-components'
import { FilterSectionTransport } from '../../../../transports/search-result-listing-static-transport'
import { trackTotalResultsCount } from '../../../event/tracking'
import SearchRegionNodeTransport from '../../../../transports/search-region-node-transport'

interface Props {
    filter: string
    isGridView: boolean
    onToggleView: () => void
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    filterStaticData: FilterSectionTransport
    gridItemsPerRow: number
    searchRegionNode: SearchRegionNodeTransport | null
}

const ListViewSection: FC<Props> = ({
    filter,
    isGridView,
    onToggleView,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    filterStaticData,
    gridItemsPerRow,
    searchRegionNode,
}) => {
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()
    const { isMobile } = useContext(ScreenContext)

    const fetchMain = useFetchSRLListings(user, filter)
    const fetchHolidu = useFetchHoliduListings(user, filter)

    const {
        sections,
        primaryTotalCount,
        primaryHeading,
        loading,
        isInitializing,
        hasNextPage,
        loadMore,
        error,
        srlNoResult,
    } = useHybridListings(fetchMain, fetchHolidu, filter)

    // Filter empty sections (Primary is always included from hook)
    const sectionsToRender = useMemo(() => {
        if (!sections || !Array.isArray(sections)) {
            return []
        }
        const filtered = sections.filter(s => s.items.length > 0)
        return filtered
    }, [sections])

    const excludedObjectKeys = useMemo(() => {
        if (isInitializing) {
            return []
        }
        return sectionsToRender.flatMap(section => section.items).map(item => item.id.toString())
    }, [isInitializing, sectionsToRender])

    const { isProvidedVariantActive: isHiddenGemActive } = useABVariant(
        AB_KEY.HIDDEN_GEM_CAROUSEL,
        AB_VARIANT_VERSION.ONE,
    )

    const { listings: hiddenGemListings, loading: hiddenGemLoading } = useHiddenGemListings(
        user,
        filter,
        excludedObjectKeys,
        isInitializing,
        isHiddenGemActive,
    )

    // Unified row-based virtualization (with skeleton support)
    const { groupCountsForRows, allRows, virtosoSections } = useMemo(() => {
        // Handle mock skeleton data
        if (isInitializing) {
            // Skeleton configuration
            const skeletonCount = LIST_VIEW_CONFIG.SKELETON_COUNT
            const rowsCount = Math.ceil(skeletonCount / gridItemsPerRow)

            // Mock rows for skeleton
            const mockRows = Array.from({ length: rowsCount }).map(() => {
                // Each row is an array of nulls (mock items)
                return Array.from({ length: gridItemsPerRow }).fill(null) as unknown as ListingSectionObject[]
            })

            return {
                groupCountsForRows: [rowsCount],
                allRows: mockRows,
                virtosoSections: [{ heading: '', key: SECTION_MERGE_KEYS.SKELETON }], // Mock section for groupContent
            }
        }

        // Handle Real Data
        const counts: number[] = []
        const rows: ListingSectionObject[][] = []

        sectionsToRender.forEach(section => {
            const chunks: ListingSectionObject[][] = []
            for (let i = 0; i < section.items.length; i += gridItemsPerRow) {
                chunks.push(section.items.slice(i, i + gridItemsPerRow))
            }
            chunks.forEach(chunk => rows.push(chunk))
            counts.push(chunks.length)
        })

        return { groupCountsForRows: counts, allRows: rows, virtosoSections: sectionsToRender }
    }, [isInitializing, sectionsToRender, isGridView, gridItemsPerRow])

    // Auto-load more if content is short (restores 'on-mount' check behavior since we don't remount Virtuoso)
    useEffect(() => {
        if (
            !isInitializing &&
            !loading &&
            !error &&
            hasNextPage && // Check if we have visible space or very few items
            // If we only have 1 row (or fewer than 5 rows), load more to fill the screen
            allRows.length < LIST_VIEW_CONFIG.AUTO_LOAD_THRESHOLD
        ) {
            loadMore()
        }
    }, [isInitializing, loading, error, hasNextPage, allRows.length, loadMore])

    useEffect(() => {
        if (!isInitializing && !loading && !error) {
            const label = searchRegionNode?.search || primaryHeading
            trackTotalResultsCount(primaryTotalCount, label)
        }
    }, [isInitializing, loading, error, primaryTotalCount, primaryHeading, searchRegionNode?.search])

    const virtosoComponents = useMemo(
        () => ({
            List: VirtuosoList,
            Footer: VirtuosoFooter,
        }),
        [],
    )

    return (
        <Container>
            <ListingHeader
                isGridView={isGridView}
                onToggleView={onToggleView}
                primaryHeading={primaryHeading}
                filterStaticData={filterStaticData}
            />
            {primaryTotalCount === 0 && !loading && !isInitializing && !!srlNoResult && (
                <ListingNoData srlNoResult={srlNoResult} />
            )}
            {error && allRows.length === 0 ? (
                <ListingErrorCard />
            ) : (
                <GroupedVirtuoso
                    useWindowScroll
                    context={{
                        isGridView,
                        loading,
                        isInitializing,
                        hasNextPage,
                        loadMore,
                        t,
                        error,
                        hasItems: allRows.length > 0,
                    }}
                    groupCounts={groupCountsForRows}
                    groupContent={(groupIndex: number) => {
                        const section = virtosoSections[groupIndex]
                        // If we are initializing, sections might be mock, so check heading existence
                        if (section?.key === SECTION_MERGE_KEYS.PRIMARY_MAIN) {
                            return <div style={{ height: '1px' }}></div>
                        }

                        if (groupIndex === 0 && !section?.heading) {
                            return <div style={{ height: '1px' }}></div>
                        }

                        return section?.heading ? <SectionHeading>{section.heading}</SectionHeading> : <SectionSpacer />
                    }}
                    itemContent={(itemIndex: number) => {
                        const row = allRows[itemIndex]
                        const isAboveFold = itemIndex < 2

                        if (isInitializing) {
                            // Render Skeleton Row
                            return <SkeletonRow isGridView={isGridView} gridItemsPerRow={gridItemsPerRow} />
                        }

                        const carouselInjectIndex = isMobile ? 3 : isGridView ? 1 : 3
                        const showCarousel =
                            isHiddenGemActive &&
                            itemIndex === carouselInjectIndex &&
                            (hiddenGemLoading || hiddenGemListings.length > 0)

                        // Render Real Row
                        return (
                            <>
                                {isGridView ? (
                                    <GridRowWrapper>
                                        {row.map((item, idx) => (
                                            <ListingCard
                                                key={idx}
                                                listing={item}
                                                index={itemIndex * gridItemsPerRow + idx}
                                                isGridView={isGridView}
                                                breadcrumbModel={breadcrumbModel}
                                                offerModel={offerModel}
                                                suitablesModel={suitablesModel}
                                                isAboveFold={isAboveFold}
                                                propagateSearchRequestId={!item.externalPartnerInfo}
                                            />
                                        ))}
                                    </GridRowWrapper>
                                ) : (
                                    <ListItemWrapper>
                                        <ListingCard
                                            listing={row[0]}
                                            index={itemIndex}
                                            isGridView={isGridView}
                                            breadcrumbModel={breadcrumbModel}
                                            offerModel={offerModel}
                                            suitablesModel={suitablesModel}
                                            isAboveFold={isAboveFold}
                                            propagateSearchRequestId={!row[0].externalPartnerInfo}
                                        />
                                    </ListItemWrapper>
                                )}
                                {showCarousel && (
                                    <HiddenGemCarousel
                                        listings={hiddenGemListings}
                                        loading={hiddenGemLoading}
                                        isGridView={isGridView}
                                        breadcrumbModel={breadcrumbModel}
                                        offerModel={offerModel}
                                        suitablesModel={suitablesModel}
                                    />
                                )}
                            </>
                        )
                    }}
                    components={virtosoComponents}
                    rangeChanged={({ endIndex }) => {
                        // Avoid triggering loadMore during initialization (skeleton)
                        if (!isInitializing && endIndex >= allRows.length - 1 && hasNextPage && !loading && !error) {
                            loadMore()
                        }
                    }}
                    atBottomThreshold={750}
                    overscan={1000}
                    increaseViewportBy={{ top: 600, bottom: 600 }}
                    style={{ height: '100vh' }}
                />
            )}
        </Container>
    )
}

export default memo(ListViewSection)
